import type { FC, RefObject, FocusEvent, MouseEvent } from 'react';
import React, { useLayoutEffect, useMemo, useRef, useState, useCallback } from 'react';
import useDebounce from 'react-use/lib/useDebounce';
import cn from 'classnames';

import type { TMainHeader } from './models/desktop';
import { useShow } from './helpers/use-show';
import { getScrollbarWidth } from './helpers/get-scrollbar-width';
import { DropDown } from './components/drop-down';

export function useHandleChangeShowSearch(
    isShowSearch: boolean,
    yandexSearchBarRef: RefObject<HTMLInputElement>
) {
    useLayoutEffect(() => {
        if (!isShowSearch) return () => {};
        // Порядок важен. Сначала высчитываем ширину, потом убираем скролл
        document.body.style.paddingRight = `${getScrollbarWidth()}px`;
        document.body.style.overflow = 'hidden';

        if (yandexSearchBarRef.current) {
            yandexSearchBarRef.current.focus();
        }

        return () => {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, [isShowSearch, yandexSearchBarRef]);
}

export const MainHeaderV2: FC<TMainHeader> = props => {
    const {
        ApplicationsButton,
        BannerLink,
        DropDownContextProvider,
        IconAlfaLogo,
        InternetBank,
        Layout,
        SearchIcon,
        Stack,
        SubsectionLink,
        TopRow,
        YandexSearchBar,
        applicationsButton,
        applicationsButtonAnalytics,
        bottomRow,
        children,
        dataTestId = 'MainHeader',
        defaultTheme = 'light',
        dropDown,
        dropDownBanners,
        height,
        internetBank,
        internetBankAnalytics,
        isSimpleHeader = false,
        logo,
        stackingOrder,
        styles,
        useNamedContainers,
        withSearch = true,
    } = props;

    const [logoRef, setLogoRef] = useState<HTMLDivElement | null>(null);
    const { isShown: isShowSearch, toggle: toggleSearch } = useShow();
    const { isShown: isShownTopRow, toggle: toggleTopRowShow } = useShow();
    const [isHovered, setHovered] = useState(false);
    const {
        applicationsButton: applicationsButtonSlot,
        internetBank: internetBankSlot,
        logo: logoSlot,
        ...promoCards
    } = useNamedContainers(children);
    const dropdownRefs = useMemo<Record<string, HTMLDivElement | null>>(() => ({}), []);
    const triangleRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const [, cancel] = useDebounce(
        () => {
            if (!isHovered && isShownTopRow) {
                toggleTopRowShow();

                return;
            }

            cancel();
        },
        400,
        [isHovered]
    );

    const yandexSearchBarRef = useRef<HTMLInputElement>(null);

    useHandleChangeShowSearch(isShowSearch, yandexSearchBarRef);

    const colorTheme = cn(styles[defaultTheme], styles.external, {
        [styles.withHeight]: height !== undefined,
    });

    const handleMouseLeave = () => setHovered(false);
    const handleMouseOver = () => setHovered(true);

    const onSubsectionLinkMouseEnter = useCallback(
        (
            e: FocusEvent<HTMLSpanElement, Element> | MouseEvent<HTMLSpanElement>,
            bottomRowItemKey: string
        ) => {
            const rect = (e.target as HTMLSpanElement).getBoundingClientRect();
            // Бранч отключен, т. к. в момент наведения ref'ы уже будут созданы,
            // а if нужен только для ts
            /* istanbul ignore else */
            if (triangleRef.current && popoverRef.current) {
                // Расчитываем позицию треугольника
                triangleRef.current.style.transform = `translateX(${
                    rect.left - popoverRef.current.getBoundingClientRect().x + rect.width / 2 - 8
                }px)`;

                // Скрываем все выпадающие меню
                Object.keys(dropdownRefs).forEach(dropDownKey => {
                    dropdownRefs[dropDownKey]?.classList.remove(styles.open);
                });

                // Скрываем общий блок для выпадающего меню
                popoverRef.current.classList.remove(styles.open);

                // Показываем нужное меню
                if (dropDown[bottomRowItemKey]) {
                    dropdownRefs[bottomRowItemKey]?.classList.add(styles.open);
                    popoverRef.current.classList.add(styles.open);
                }
            }
        },
        [dropDown, styles, dropdownRefs]
    );

    const onBottomRowMouseLeave = useCallback(() => {
        Object.keys(dropdownRefs).forEach(key => {
            dropdownRefs[key]?.classList.remove(styles.open);
        });
        popoverRef.current?.classList.remove(styles.open);
    }, [dropdownRefs, styles]);

    const renderBottomRow = useMemo(
        () => (
            <DropDownContextProvider leftShift={logoRef?.offsetWidth}>
                <section>
                    {bottomRow.map(({ analytics, key, link, title }) => (
                        <SubsectionLink
                            key={key}
                            analytics={analytics}
                            hasDropDown={!!dropDown[key]}
                            link={link}
                            onEnter={e => onSubsectionLinkMouseEnter(e, key)}
                            title={title}
                        />
                    ))}
                </section>
                <section
                    ref={popoverRef}
                    className={styles.popover}
                    data-test-id={`${dataTestId}-popover-container`}
                >
                    <div ref={triangleRef} className={styles.triangle} />
                    <div className={styles.triangleAfter} />
                    {bottomRow.map(({ key }) => (
                        <DropDown
                            key={`dropdown-${key}`}
                            banner={promoCards[key]}
                            containerRef={ref => {
                                dropdownRefs[key] = ref;
                            }}
                            dropDownBannerData={dropDownBanners[key]}
                            dropDownData={dropDown[key]}
                        />
                    ))}
                </section>
            </DropDownContextProvider>
        ),
        [
            bottomRow,
            dropDown,
            dropDownBanners,
            logoRef,
            DropDownContextProvider,
            promoCards,
            SubsectionLink,
            styles,
            dropdownRefs,
            onSubsectionLinkMouseEnter,
            dataTestId,
        ]
    );

    const wrapperStyle = { height };

    return (
        <Stack value={stackingOrder.OVERLAY}>
            {(zIndex: number) => (
                <Layout
                    className={colorTheme}
                    dataTestId={`${dataTestId}Layout`}
                    style={{ ...wrapperStyle, zIndex: zIndex }}
                >
                    <div className={colorTheme} style={wrapperStyle}>
                        <div
                            className={styles.container}
                            data-test-id={`${dataTestId}-container`}
                            onMouseEnter={handleMouseOver}
                            onMouseLeave={handleMouseLeave}
                        >
                            <div ref={setLogoRef} className={styles.containerLogo}>
                                {logoSlot || (
                                    <BannerLink link={logo?.link} title={logo?.title}>
                                        <IconAlfaLogo
                                            className={styles.iconLogo}
                                            data-test-id={`${dataTestId}Logo`}
                                            height={47}
                                            width={34}
                                        />
                                    </BannerLink>
                                )}
                            </div>
                            {!isSimpleHeader && (
                                <>
                                    <div className={styles.containerLeft}>
                                        <TopRow
                                            handleClick={toggleTopRowShow}
                                            showAll={isShownTopRow}
                                            theme={defaultTheme}
                                            {...props}
                                        />
                                        <div onMouseLeave={onBottomRowMouseLeave}>
                                            {renderBottomRow}
                                        </div>
                                    </div>
                                    <div
                                        className={cn(styles.containerRight, {
                                            [styles.hideContainerRight]: isShownTopRow,
                                        })}
                                        data-test-id={`${dataTestId}-container-right`}
                                    >
                                        <div
                                            className={cn(styles.controls, {
                                                [styles.fadeOut]: isShowSearch,
                                            })}
                                        >
                                            {withSearch && <SearchIcon onClick={toggleSearch} />}
                                            {applicationsButton ? (
                                                <ApplicationsButton
                                                    analytics={applicationsButtonAnalytics}
                                                    applicationsButton={applicationsButton}
                                                    theme={defaultTheme}
                                                />
                                            ) : (
                                                applicationsButtonSlot
                                            )}

                                            {internetBank ? (
                                                <InternetBank
                                                    analytics={internetBankAnalytics}
                                                    internetBank={internetBank}
                                                />
                                            ) : (
                                                internetBankSlot
                                            )}
                                        </div>
                                        <div
                                            className={cn(styles.searchHolder, {
                                                [styles.fadeIn]: isShowSearch,
                                            })}
                                        >
                                            <YandexSearchBar inputInnerRef={yandexSearchBarRef} />
                                        </div>
                                        {
                                            // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                                            <div
                                                className={cn(styles.searchOverlay, {
                                                    [styles.fadeIn]: isShowSearch,
                                                })}
                                                data-test-id="main-header-search-overlay"
                                                onClick={toggleSearch}
                                            />
                                        }
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </Layout>
            )}
        </Stack>
    );
};
